import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronRight, ChevronDown, ChevronLeft, Check, Loader2,
  MapPin, Search, Package, ArrowRight, Download,
  ShieldCheck, AlertTriangle, ShieldAlert, CheckCircle2,
  Calendar, Clock, Trash2, Play, Pause, Map as MapIcon,
  TableProperties, X, Plus, TrendingUp, BarChart3,
  Activity, Zap, ArrowLeft, Settings2, Eye,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import AppHeader from "./AppHeader";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, ReferenceLine, Legend,
} from "recharts";
import IndiaMap from "./IndiaMap";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const parsePrice = (priceStr) => {
  if (!priceStr) return null;
  const cleaned = priceStr.replace(/[^\d.]/g, "");
  const val = parseFloat(cleaned);
  return isNaN(val) ? null : val;
};

const formatCurrency = (val) => {
  if (val === null || val === undefined) return "-";
  return `₹${val.toLocaleString("en-IN")}`;
};

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const getTimeAgo = (date) => {
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

// ─── BRAND CARDS ───
const BrandCards = ({ brands, selectedBrand, onSelect }) => {
  if (!brands.length) return <div className="text-center py-12 text-gray-400 text-sm">No brands found. Add brands in Control Panel.</div>;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="brand-cards">
      {brands.map(brand => {
        const isSelected = selectedBrand?.brand_id === brand.brand_id;
        return (
          <div key={brand.brand_id} onClick={() => onSelect(brand)}
            className={`relative border-2 p-4 cursor-pointer transition-all duration-150 rounded-lg ${
              isSelected ? "border-[#32B4AC] bg-[#F0FDFA] shadow-sm" : "border-gray-200 bg-white hover:border-gray-300"
            }`} data-testid={`brand-card-${brand.brand_id}`}>
            {isSelected && (
              <div className="absolute top-2 right-2 w-5 h-5 bg-[#32B4AC] rounded-full flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </div>
            )}
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gray-100 to-gray-50 border border-gray-200 flex items-center justify-center mb-3">
              {brand.icon_url ? (
                <img src={brand.icon_url} alt="" className="w-6 h-6 object-contain" />
              ) : (
                <span className="text-lg font-bold text-gray-400">{brand.name[0]}</span>
              )}
            </div>
            <h3 className="font-semibold text-sm text-gray-900">{brand.name}</h3>
            <p className="text-[11px] text-gray-400 mt-0.5">{brand.product_count || 0} products</p>
          </div>
        );
      })}
    </div>
  );
};

// ─── PRODUCT SELECTOR ───
const ProductSelector = ({ products, selectedProducts, onToggle, onSelectAll }) => {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all, with-map, no-map, amazon, flipkart
  const [typeFilter, setTypeFilter] = useState("all");
  const [formFactorFilter, setFormFactorFilter] = useState("all");

  const allSelected = products.length > 0 && selectedProducts.length === products.length;

  // Gather unique form factors and types from products
  const allFormFactors = [...new Set(products.map(p => p.form_factor).filter(Boolean))].sort();
  const allTypes = [...new Set(products.map(p => p.product_type).filter(Boolean))].sort();

  const filtered = products.filter(product => {
    const s = search.toLowerCase();
    const nameMatch = !s || product.name.toLowerCase().includes(s) || (product.ean || "").toLowerCase().includes(s);
    if (!nameMatch) return false;

    if (typeFilter !== "all" && product.product_type !== typeFilter) return false;
    if (formFactorFilter !== "all" && product.form_factor !== formFactorFilter) return false;

    const platforms = Object.keys(product.platform_urls || {});
    const hasMap = Object.keys(product.decided_prices || {}).length > 0;
    if (filter === "with-map") return hasMap;
    if (filter === "no-map") return !hasMap;
    if (filter === "amazon") return platforms.includes("amazon");
    if (filter === "flipkart") return platforms.includes("flipkart");
    if (filter === "blinkit") return platforms.includes("blinkit");
    return true;
  });

  const filteredSelected = filtered.filter(p => selectedProducts.includes(p.product_id)).length;
  const allFilteredSelected = filtered.length > 0 && filteredSelected === filtered.length;

  const handleSelectFiltered = () => {
    if (allFilteredSelected) {
      // Deselect only the filtered products
      const filteredIds = new Set(filtered.map(p => p.product_id));
      onToggle("__batch_deselect__", [...filteredIds]);
    } else {
      // Select all filtered products
      const filteredIds = filtered.map(p => p.product_id);
      onToggle("__batch_select__", filteredIds);
    }
  };

  const FILTERS = [
    { key: "all", label: "All" },
    { key: "with-map", label: "With MAP" },
    { key: "no-map", label: "No MAP" },
    { key: "amazon", label: "Amazon" },
    { key: "flipkart", label: "Flipkart" },
    { key: "blinkit", label: "Blinkit" },
  ];

  const TYPE_COLORS = {
    'Unit': { bg: 'bg-blue-50', active: 'bg-blue-100 border-blue-400 text-blue-700', text: 'text-blue-600', dot: 'bg-blue-500' },
    'Combos': { bg: 'bg-amber-50', active: 'bg-amber-100 border-amber-400 text-amber-700', text: 'text-amber-600', dot: 'bg-amber-500' },
    'Multi-Brand Combo': { bg: 'bg-purple-50', active: 'bg-purple-100 border-purple-400 text-purple-700', text: 'text-purple-600', dot: 'bg-purple-500' },
    'Merchandise Combo': { bg: 'bg-pink-50', active: 'bg-pink-100 border-pink-400 text-pink-700', text: 'text-pink-600', dot: 'bg-pink-500' },
  };

  const hasActiveFilters = filter !== "all" || typeFilter !== "all" || formFactorFilter !== "all" || !!search;

  return (
    <div data-testid="product-selector">
      {/* Search */}
      <div className="mb-3 space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search by product name or EAN..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 text-sm"
            data-testid="product-search-input"
          />
          {search && (
            <button onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Filter Groups */}
        <div className="bg-gray-50/70 rounded-xl border border-gray-100 p-3 space-y-3">
          {/* Platform / MAP Row */}
          <div>
            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Platform</div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {FILTERS.map(f => {
                const count = f.key === "all" ? products.length
                  : f.key === "with-map" ? products.filter(p => Object.keys(p.decided_prices || {}).length > 0).length
                  : f.key === "no-map" ? products.filter(p => Object.keys(p.decided_prices || {}).length === 0).length
                  : f.key === "amazon" ? products.filter(p => Object.keys(p.platform_urls || {}).includes("amazon")).length
                  : f.key === "flipkart" ? products.filter(p => Object.keys(p.platform_urls || {}).includes("flipkart")).length
                  : products.filter(p => Object.keys(p.platform_urls || {}).includes("blinkit")).length;
                return (
                  <button key={f.key} onClick={() => setFilter(f.key)}
                    className={`text-[11px] px-2.5 py-1.5 rounded-lg font-medium transition-all duration-150 border flex items-center gap-1.5 ${
                      filter === f.key
                        ? f.key === "amazon" ? "bg-orange-50 border-orange-300 text-orange-700 shadow-sm"
                        : f.key === "flipkart" ? "bg-yellow-50 border-yellow-300 text-yellow-700 shadow-sm"
                        : "bg-[#E8F8F5] border-[#32B4AC] text-[#32B4AC] shadow-sm"
                        : "bg-white border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50"
                    }`} data-testid={`product-filter-${f.key}`}>
                    {f.label}
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full tabular-nums ${
                      filter === f.key ? "bg-white/60" : "bg-gray-100"
                    }`}>{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Product Type Row */}
          {allTypes.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Type</div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <button onClick={() => setTypeFilter("all")}
                  className={`text-[11px] px-2.5 py-1.5 rounded-lg font-medium transition-all duration-150 border flex items-center gap-1.5 ${
                    typeFilter === "all"
                      ? "bg-[#E8F8F5] border-[#32B4AC] text-[#32B4AC] shadow-sm"
                      : "bg-white border-gray-200 text-gray-500 hover:border-gray-300"
                  }`} data-testid="product-filter-type-all">
                  All
                </button>
                {allTypes.map(t => {
                  const tc = TYPE_COLORS[t] || { active: 'bg-gray-100 border-gray-400 text-gray-700', dot: 'bg-gray-500' };
                  const count = products.filter(p => p.product_type === t).length;
                  return (
                    <button key={t} onClick={() => setTypeFilter(typeFilter === t ? "all" : t)}
                      className={`text-[11px] px-2.5 py-1.5 rounded-lg font-medium transition-all duration-150 border flex items-center gap-1.5 ${
                        typeFilter === t
                          ? `${tc.active} shadow-sm`
                          : "bg-white border-gray-200 text-gray-500 hover:border-gray-300"
                      }`} data-testid={`product-filter-type-${t}`}>
                      <div className={`w-2 h-2 rounded-full ${tc.dot}`} />
                      {t}
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full tabular-nums ${
                        typeFilter === t ? "bg-white/60" : "bg-gray-100"
                      }`}>{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Form Factor Row */}
          {allFormFactors.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Form Factor</div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <button onClick={() => setFormFactorFilter("all")}
                  className={`text-[11px] px-2.5 py-1.5 rounded-lg font-medium transition-all duration-150 border flex items-center gap-1.5 ${
                    formFactorFilter === "all"
                      ? "bg-[#E8F8F5] border-[#32B4AC] text-[#32B4AC] shadow-sm"
                      : "bg-white border-gray-200 text-gray-500 hover:border-gray-300"
                  }`} data-testid="product-filter-ff-all">
                  All
                </button>
                {allFormFactors.map(ff => {
                  const count = products.filter(p => p.form_factor === ff).length;
                  return (
                    <button key={ff} onClick={() => setFormFactorFilter(formFactorFilter === ff ? "all" : ff)}
                      className={`text-[11px] px-2.5 py-1.5 rounded-lg font-medium transition-all duration-150 border flex items-center gap-1.5 ${
                        formFactorFilter === ff
                          ? "bg-indigo-50 border-indigo-300 text-indigo-700 shadow-sm"
                          : "bg-white border-gray-200 text-gray-500 hover:border-gray-300"
                      }`} data-testid={`product-filter-ff-${ff}`}>
                      {ff}
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full tabular-nums ${
                        formFactorFilter === ff ? "bg-white/60" : "bg-gray-100"
                      }`}>{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Status bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">
              {filteredSelected}/{filtered.length} shown · <span className="font-medium text-[#32B4AC]">{selectedProducts.length}</span> selected
            </span>
            {hasActiveFilters && (
              <button onClick={() => { setSearch(""); setFilter("all"); setTypeFilter("all"); setFormFactorFilter("all"); }}
                className="text-[10px] text-red-400 hover:text-red-600 flex items-center gap-0.5 ml-1">
                <X className="w-3 h-3" /> Clear all
              </button>
            )}
          </div>
          <button onClick={hasActiveFilters ? handleSelectFiltered : onSelectAll}
            className="text-xs text-[#32B4AC] hover:underline font-medium" data-testid="select-all-products">
            {hasActiveFilters ? (allFilteredSelected ? "Deselect Filtered" : "Select Filtered") : (allSelected ? "Deselect All" : "Select All")}
          </button>
        </div>
      </div>

      {/* Product List */}
      <div className="max-h-[400px] overflow-y-auto space-y-2 pr-1 scrollbar-thin" data-testid="product-list-scroll">
        {filtered.length === 0 ? (
          <div className="text-center py-10">
            <Search className="w-8 h-8 text-gray-200 mx-auto mb-2" />
            <div className="text-sm text-gray-400">No products match "{search || filter}"</div>
            <button onClick={() => { setSearch(""); setFilter("all"); setTypeFilter("all"); setFormFactorFilter("all"); }}
              className="text-xs text-[#32B4AC] mt-1 hover:underline">Clear filters</button>
          </div>
        ) : (
          filtered.map(product => {
            const isSelected = selectedProducts.includes(product.product_id);
            const platforms = Object.keys(product.platform_urls || {});
            const dp = product.decided_prices || {};
            return (
              <div key={product.product_id} onClick={() => onToggle(product.product_id)}
                className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all duration-150 ${
                  isSelected ? "border-[#32B4AC] bg-[#F0FDFA]" : "border-gray-100 bg-white hover:border-gray-200"
                }`} data-testid={`product-item-${product.product_id}`}>
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                  isSelected ? "bg-[#32B4AC] border-[#32B4AC]" : "border-gray-300"
                }`}>
                  {isSelected && <Check className="w-3 h-3 text-white" />}
                </div>
                {product.image_url ? (
                  <img src={product.image_url} alt="" className="w-10 h-10 rounded-lg object-cover border border-gray-200" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                    <Package className="w-5 h-5 text-gray-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-gray-900 truncate">{product.name}</div>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    {product.product_type && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                        product.product_type === 'Unit' ? 'bg-blue-100 text-blue-700' :
                        product.product_type === 'Combos' ? 'bg-amber-100 text-amber-700' :
                        product.product_type === 'Multi-Brand Combo' ? 'bg-purple-100 text-purple-700' :
                        'bg-pink-100 text-pink-700'
                      }`}>{product.product_type}</span>
                    )}
                    {product.form_factor && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">{product.form_factor}</span>
                    )}
                    {platforms.map(p => (
                      <span key={p} className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                        p === 'amazon' ? 'bg-orange-100 text-orange-600' : p === 'flipkart' ? 'bg-yellow-100 text-yellow-700' : p === 'blinkit' ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-500'
                      }`}>{p}</span>
                    ))}
                    {platforms.length === 0 && <span className="text-[10px] text-red-400">No platform URLs</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {Object.keys(dp).length > 0 ? (
                    <div className="space-y-0.5">
                      {Object.entries(dp).map(([plat, price]) => (
                        <div key={plat} className="text-[10px] text-gray-500">
                          <span className="uppercase">{plat.slice(0, 3)}</span> MAP: <span className="font-medium text-gray-700">₹{price}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-[10px] text-amber-500">No MAP set</span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// ─── PINCODE SELECTOR ───
const PincodeSelector = ({ pincodes, selectedPincodes, onTogglePincode, onToggleCity, onToggleState }) => {
  const [search, setSearch] = useState("");
  const [expandedStates, setExpandedStates] = useState({});
  const [expandedCities, setExpandedCities] = useState({});

  const grouped = {};
  pincodes.forEach(p => {
    if (!grouped[p.state]) grouped[p.state] = {};
    if (!grouped[p.state][p.city]) grouped[p.state][p.city] = [];
    grouped[p.state][p.city].push(p.pincode);
  });

  const filteredStates = Object.keys(grouped).filter(state => {
    if (!search) return true;
    const s = search.toLowerCase();
    if (state.toLowerCase().includes(s)) return true;
    return Object.keys(grouped[state]).some(city =>
      city.toLowerCase().includes(s) || grouped[state][city].some(pc => pc.includes(s))
    );
  }).sort();

  return (
    <div data-testid="pincode-selector">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-gray-600">{selectedPincodes.length} pincodes selected</span>
      </div>
      <div className="relative mb-3">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input placeholder="Search state, city, or pincode..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 text-sm" />
      </div>
      <div className="max-h-[350px] overflow-y-auto space-y-1.5 pr-1">
        {filteredStates.map(state => {
          const statePins = Object.values(grouped[state]).flat();
          const stateSelected = statePins.filter(pc => selectedPincodes.includes(pc)).length;
          const isExpanded = expandedStates[state];
          return (
            <div key={state} className="rounded-lg border border-gray-200 overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => setExpandedStates(prev => ({ ...prev, [state]: !prev[state] }))}>
                {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 cursor-pointer ${
                  stateSelected === statePins.length ? "bg-[#32B4AC] border-[#32B4AC]" : stateSelected > 0 ? "bg-[#32B4AC]/30 border-[#32B4AC]" : "border-gray-300"
                }`} onClick={(e) => { e.stopPropagation(); onToggleState(state, statePins); }}>
                  {stateSelected === statePins.length && <Check className="w-3 h-3 text-white" />}
                </div>
                <MapPin className="w-3.5 h-3.5 text-[#32B4AC]" />
                <span className="font-medium text-sm text-gray-900">{state}</span>
                <span className="text-[10px] text-gray-400">{stateSelected}/{statePins.length}</span>
              </div>
              {isExpanded && Object.keys(grouped[state]).sort().map(city => {
                const cityPins = grouped[state][city];
                const cityKey = `${state}-${city}`;
                const citySelected = cityPins.filter(pc => selectedPincodes.includes(pc)).length;
                const isCityExpanded = expandedCities[cityKey];
                return (
                  <div key={cityKey} className="ml-4 border-l border-gray-200">
                    <div className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => setExpandedCities(prev => ({ ...prev, [cityKey]: !prev[cityKey] }))}>
                      {isCityExpanded ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 cursor-pointer ${
                        citySelected === cityPins.length ? "bg-[#32B4AC] border-[#32B4AC]" : citySelected > 0 ? "bg-[#32B4AC]/30 border-[#32B4AC]" : "border-gray-300"
                      }`} onClick={(e) => { e.stopPropagation(); onToggleCity(cityPins); }}>
                        {citySelected === cityPins.length && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className="text-sm text-gray-700">{city}</span>
                      <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{citySelected}/{cityPins.length}</span>
                    </div>
                    {isCityExpanded && (
                      <div className="flex flex-wrap gap-1.5 px-6 py-2">
                        {cityPins.sort().map(pc => {
                          const isSelected = selectedPincodes.includes(pc);
                          return (
                            <button key={pc} onClick={() => onTogglePincode(pc)}
                              className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${
                                isSelected ? "bg-[#32B4AC] text-white border-[#32B4AC]" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                              }`} data-testid={`pincode-chip-${pc}`}
                            >{pc}</button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
        {filteredStates.length === 0 && <div className="text-center py-8 text-gray-400 text-sm">No pincodes found. Add pincodes in Control Panel.</div>}
      </div>
    </div>
  );
};

// ─── PRICE TREND CHART ───
const PLATFORM_COLORS = { amazon: "#F97316", flipkart: "#EAB308", blinkit: "#F59E0B" };
const RANGE_OPTIONS = [
  { label: "7D", value: 7 },
  { label: "30D", value: 30 },
  { label: "90D", value: 90 },
];

const PriceTrendChart = ({ productIds, tracking }) => {
  const [trendData, setTrendData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(30);
  const [selectedProduct, setSelectedProduct] = useState("all");

  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ days: range });
        if (productIds?.length) params.set("product_ids", productIds.join(","));
        const res = await axios.get(`${API}/price-governance/history?${params}`);
        setTrendData(res.data || []);
      } catch { }
      setLoading(false);
    };
    fetchHistory();
  }, [range, productIds]);

  const products = useMemo(() => {
    const map = {};
    trendData.forEach(d => { map[d.product_id] = d.product_name; });
    return Object.entries(map).map(([id, name]) => ({ id, name }));
  }, [trendData]);

  const filteredData = useMemo(() => {
    if (selectedProduct === "all") return trendData;
    return trendData.filter(d => d.product_id === selectedProduct);
  }, [trendData, selectedProduct]);

  const chartData = useMemo(() => {
    const dateMap = {};
    filteredData.forEach(d => {
      if (!dateMap[d.date]) dateMap[d.date] = { date: d.date };
      const key = `${d.product_name} (${d.platform === "amazon" ? "AMZ" : d.platform === "flipkart" ? "FK" : "BK"})`;
      dateMap[d.date][key] = d.avg_price;
      dateMap[d.date][`${key}_breach`] = d.breach_count;
      dateMap[d.date][`${key}_map`] = d.decided_price;
    });
    return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredData]);

  const lineKeys = useMemo(() => {
    const keys = new Set();
    filteredData.forEach(d => {
      keys.add(`${d.product_name} (${d.platform === "amazon" ? "AMZ" : "FK"})`);
    });
    return [...keys];
  }, [filteredData]);

  const mapValues = useMemo(() => {
    const map = {};
    filteredData.forEach(d => {
      const key = `${d.product_name} (${d.platform === "amazon" ? "AMZ" : d.platform === "flipkart" ? "FK" : "BK"})`;
      if (d.decided_price) map[key] = d.decided_price;
    });
    return map;
  }, [filteredData]);

  const breachSummary = useMemo(() => {
    const summary = {};
    filteredData.forEach(d => {
      if (!summary[d.product_id]) summary[d.product_id] = { name: d.product_name, total: 0, breaches: 0, scans: 0 };
      summary[d.product_id].total += d.total_checks;
      summary[d.product_id].breaches += d.breach_count;
      summary[d.product_id].scans++;
    });
    return Object.values(summary);
  }, [filteredData]);

  const lineColorPalette = [
    "#F97316", "#EAB308", "#3B82F6", "#8B5CF6", "#EC4899", "#10B981", "#06B6D4", "#F43F5E",
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-[#32B4AC]" />
        <span className="ml-2 text-sm text-gray-500">Loading historical data...</span>
      </div>
    );
  }

  if (trendData.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
        <TrendingUp className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <div className="text-sm text-gray-500 font-medium">No historical data yet</div>
        <div className="text-xs text-gray-400 mt-1">Run Price Governance scans to build trend data over time</div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-lg shadow-lg p-3 text-xs">
        <div className="font-semibold text-gray-700 mb-1.5">{label}</div>
        {payload.map((p, i) => (
          <div key={i} className="flex items-center gap-2 py-0.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
            <span className="text-gray-600">{p.name}:</span>
            <span className="font-semibold text-gray-900">{formatCurrency(p.value)}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div data-testid="price-trend-chart">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <select value={selectedProduct} onChange={e => setSelectedProduct(e.target.value)}
            className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:border-[#32B4AC]"
            data-testid="trend-product-filter">
            <option value="all">All Products</option>
            {products.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
          {RANGE_OPTIONS.map(r => (
            <button key={r.value} onClick={() => setRange(r.value)}
              className={`text-xs px-3 py-1 rounded-md font-medium transition-colors ${
                range === r.value ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`} data-testid={`trend-range-${r.value}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-5 mb-4">
        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-4">Average Price Over Time</div>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#9CA3AF" }} tickFormatter={v => {
              const parts = v.split("-");
              return `${parts[2]}/${parts[1]}`;
            }} />
            <YAxis tick={{ fontSize: 10, fill: "#9CA3AF" }} tickFormatter={v => `₹${v}`} />
            <RechartsTooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {lineKeys.map((key, i) => (
              <Line key={key} type="monotone" dataKey={key}
                stroke={lineColorPalette[i % lineColorPalette.length]}
                strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }}
                connectNulls />
            ))}
            {Object.entries(mapValues).map(([key, val], i) => (
              <ReferenceLine key={`map-${key}`} y={val}
                stroke={lineColorPalette[lineKeys.indexOf(key) % lineColorPalette.length]}
                strokeDasharray="6 3" strokeOpacity={0.5}
                label={{ value: `MAP ₹${val}`, position: "right", fontSize: 9, fill: "#EF4444" }} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {breachSummary.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/60">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Breach History ({range} days)</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-5 py-2.5 text-[10px] font-medium text-gray-400 uppercase tracking-wider">Product</th>
                <th className="text-right px-4 py-2.5 text-[10px] font-medium text-gray-400 uppercase tracking-wider">Scans</th>
                <th className="text-right px-4 py-2.5 text-[10px] font-medium text-gray-400 uppercase tracking-wider">Checks</th>
                <th className="text-right px-4 py-2.5 text-[10px] font-medium text-gray-400 uppercase tracking-wider">Breaches</th>
                <th className="text-right px-4 py-2.5 text-[10px] font-medium text-gray-400 uppercase tracking-wider">Rate</th>
              </tr>
            </thead>
            <tbody>
              {breachSummary.map((s, i) => {
                const rate = s.total > 0 ? ((s.breaches / s.total) * 100).toFixed(1) : 0;
                return (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="px-5 py-2.5 text-xs font-medium text-gray-900">{s.name}</td>
                    <td className="px-4 py-2.5 text-right text-xs text-gray-600 tabular-nums">{s.scans}</td>
                    <td className="px-4 py-2.5 text-right text-xs text-gray-600 tabular-nums">{s.total}</td>
                    <td className="px-4 py-2.5 text-right">
                      <span className={`text-xs font-semibold tabular-nums ${s.breaches > 0 ? "text-red-600" : "text-green-600"}`}>{s.breaches}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${
                        parseFloat(rate) > 20 ? "bg-red-100 text-red-700" :
                        parseFloat(rate) > 0 ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"
                      }`}>{rate}%</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── BREACH RESULTS ───
const BreachResults = ({ results, tracking, allPincodesMaster }) => {
  const [viewMode, setViewMode] = useState("table");
  const [showMap, setShowMap] = useState(false);
  const [showTrends, setShowTrends] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState({});
  const [expandedStates, setExpandedStates] = useState({});

  const productMap = {};
  tracking.forEach(t => { productMap[t.product_id] = t; });

  const byProduct = {};
  results.forEach(r => {
    if (!byProduct[r.product_id]) byProduct[r.product_id] = { info: productMap[r.product_id] || r, entries: [] };
    byProduct[r.product_id].entries.push(r);
  });

  const allPincodes = [...new Set(results.map(r => r.pincode))].sort();

  const pincodeToState = {};
  (allPincodesMaster || []).forEach(p => { pincodeToState[p.pincode] = p.state; });

  const getComparison = (entries, pincode, decidedPrices) => {
    const amz = entries.find(e => e.platform === "amazon" && e.pincode === pincode);
    const fk = entries.find(e => e.platform === "flipkart" && e.pincode === pincode);
    const bk = entries.find(e => e.platform === "blinkit" && e.pincode === pincode);
    const amzPrice = parsePrice(amz?.price);
    const fkPrice = parsePrice(fk?.price);
    const bkPrice = parsePrice(bk?.price);
    const amzDecided = decidedPrices?.amazon ? parseFloat(decidedPrices.amazon) : null;
    const fkDecided = decidedPrices?.flipkart ? parseFloat(decidedPrices.flipkart) : null;
    const bkDecided = decidedPrices?.blinkit ? parseFloat(decidedPrices.blinkit) : null;
    const amzBreach = amzPrice !== null && amzDecided !== null && amzPrice < amzDecided;
    const fkBreach = fkPrice !== null && fkDecided !== null && fkPrice < fkDecided;
    const bkBreach = bkPrice !== null && bkDecided !== null && bkPrice < bkDecided;
    const allPrices = [amzPrice, fkPrice, bkPrice].filter(p => p !== null);
    const diff = allPrices.length >= 2 ? Math.abs(Math.max(...allPrices) - Math.min(...allPrices)) : null;
    const diffPct = (diff !== null && Math.max(...allPrices) > 0) ? ((diff / Math.max(...allPrices)) * 100).toFixed(1) : null;
    const scrapedAt = amz?.scraped_at || fk?.scraped_at || bk?.scraped_at || null;
    return { amazon: amz, flipkart: fk, blinkit: bk, amzPrice, fkPrice, bkPrice, amzDecided, fkDecided, bkDecided, amzBreach, fkBreach, bkBreach, diff, diffPct, scrapedAt };
  };

  const totalProducts = Object.keys(byProduct).length;
  let totalBreaches = 0;
  let totalCompliant = 0;
  const breachDetails = [];

  Object.values(byProduct).forEach(({ info, entries }) => {
    const dp = info.decided_prices || {};
    allPincodes.forEach(pc => {
      const comp = getComparison(entries, pc, dp);
      if (comp.amzBreach) { totalBreaches++; breachDetails.push({ product: info.product_name, platform: "amazon", pincode: pc, price: comp.amzPrice, decided_price: comp.amzDecided, state: pincodeToState[pc] || "Unknown", scraped_at: comp.amazon?.scraped_at }); }
      else if (comp.amzPrice !== null && comp.amzDecided !== null) { totalCompliant++; }
      if (comp.fkBreach) { totalBreaches++; breachDetails.push({ product: info.product_name, platform: "flipkart", pincode: pc, price: comp.fkPrice, decided_price: comp.fkDecided, state: pincodeToState[pc] || "Unknown", scraped_at: comp.flipkart?.scraped_at }); }
      else if (comp.fkPrice !== null && comp.fkDecided !== null) { totalCompliant++; }
      if (comp.bkBreach) { totalBreaches++; breachDetails.push({ product: info.product_name, platform: "blinkit", pincode: pc, price: comp.bkPrice, decided_price: comp.bkDecided, state: pincodeToState[pc] || "Unknown", scraped_at: comp.blinkit?.scraped_at }); }
      else if (comp.bkPrice !== null && comp.bkDecided !== null) { totalCompliant++; }
    });
  });

  const breachByState = {};
  const pincodeDetailsByState = {};
  breachDetails.forEach(b => {
    breachByState[b.state] = (breachByState[b.state] || 0) + 1;
    if (!pincodeDetailsByState[b.state]) pincodeDetailsByState[b.state] = [];
    pincodeDetailsByState[b.state].push({ pincode: b.pincode, product: b.product, platform: b.platform, price: b.price, decided_price: b.decided_price, breach: true, scraped_at: b.scraped_at });
  });
  Object.values(byProduct).forEach(({ info, entries }) => {
    const dp = info.decided_prices || {};
    allPincodes.forEach(pc => {
      const comp = getComparison(entries, pc, dp);
      const st = pincodeToState[pc] || "Unknown";
      if (!pincodeDetailsByState[st]) pincodeDetailsByState[st] = [];
      if (comp.amzPrice !== null && comp.amzDecided !== null && !comp.amzBreach) {
        pincodeDetailsByState[st].push({ pincode: pc, product: info.product_name, platform: "amazon", price: comp.amzPrice, decided_price: comp.amzDecided, breach: false, scraped_at: comp.amazon?.scraped_at });
      }
      if (comp.fkPrice !== null && comp.fkDecided !== null && !comp.fkBreach) {
        pincodeDetailsByState[st].push({ pincode: pc, product: info.product_name, platform: "flipkart", price: comp.fkPrice, decided_price: comp.fkDecided, breach: false, scraped_at: comp.flipkart?.scraped_at });
      }
      if (comp.bkPrice !== null && comp.bkDecided !== null && !comp.bkBreach) {
        pincodeDetailsByState[st].push({ pincode: pc, product: info.product_name, platform: "blinkit", price: comp.bkPrice, decided_price: comp.bkDecided, breach: false, scraped_at: comp.blinkit?.scraped_at });
      }
    });
  });

  const handleExportCSV = () => {
    const headers = ["Product", "Brand", "Platform", "Pincode", "State", "Scraped Price", "MAP (Decided Price)", "Breach", "MRP", "Availability", "Scraped At"];
    const rows = [];
    Object.values(byProduct).forEach(({ info, entries }) => {
      const dp = info.decided_prices || {};
      allPincodes.forEach(pc => {
        const comp = getComparison(entries, pc, dp);
        if (comp.amazon) {
          rows.push([info.product_name, info.brand_name, "Amazon", pc, pincodeToState[pc] || "", comp.amzPrice ?? "", comp.amzDecided ?? "", comp.amzBreach ? "YES" : (comp.amzDecided ? "NO" : "N/A"), comp.amazon.mrp || "", comp.amazon.availability || "", comp.amazon.scraped_at || ""]);
        }
        if (comp.flipkart) {
          rows.push([info.product_name, info.brand_name, "Flipkart", pc, pincodeToState[pc] || "", comp.fkPrice ?? "", comp.fkDecided ?? "", comp.fkBreach ? "YES" : (comp.fkDecided ? "NO" : "N/A"), comp.flipkart.mrp || "", comp.flipkart.availability || "", comp.flipkart.scraped_at || ""]);
        }
        if (comp.blinkit) {
          rows.push([info.product_name, info.brand_name, "Blinkit", pc, pincodeToState[pc] || "", comp.bkPrice ?? "", comp.bkDecided ?? "", comp.bkBreach ? "YES" : (comp.bkDecided ? "NO" : "N/A"), comp.blinkit.mrp || "", comp.blinkit.availability || "", comp.blinkit.scraped_at || ""]);
        }
      });
    });
    const csv = [headers.join(","), ...rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `price_governance_breaches_${Date.now()}.csv`;
    link.click();
  };

  return (
    <div data-testid="price-results">
      {/* Summary Stats Row */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Products</div>
          <div className="text-2xl font-bold text-gray-900 tabular-nums">{totalProducts}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Pincodes</div>
          <div className="text-2xl font-bold text-gray-900 tabular-nums">{allPincodes.length}</div>
        </div>
        <div className={`rounded-lg border p-4 ${totalBreaches > 0 ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}>
          <div className={`text-xs font-medium uppercase tracking-wider mb-1 ${totalBreaches > 0 ? "text-red-400" : "text-emerald-400"}`}>Breaches</div>
          <div className={`text-2xl font-bold flex items-center gap-2 tabular-nums ${totalBreaches > 0 ? "text-red-600" : "text-emerald-600"}`}>
            {totalBreaches > 0 ? <ShieldAlert className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
            {totalBreaches}
          </div>
        </div>
        <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-4">
          <div className="text-xs font-medium text-emerald-400 uppercase tracking-wider mb-1">Compliant</div>
          <div className="text-2xl font-bold text-emerald-600 tabular-nums">{totalCompliant}</div>
        </div>
      </div>

      {/* View Tabs */}
      <div className="flex items-center border-b border-gray-200 mb-5">
        <button onClick={() => { setViewMode("table"); setShowMap(false); setShowTrends(false); }}
          className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5 -mb-px ${
            !showMap && !showTrends ? "border-[#32B4AC] text-[#32B4AC]" : "border-transparent text-gray-400 hover:text-gray-600"
          }`} data-testid="view-table-btn">
          <TableProperties className="w-3.5 h-3.5" /> Comparison
        </button>
        <button onClick={() => { setShowMap(true); setShowTrends(false); }}
          className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5 -mb-px ${
            showMap ? "border-[#32B4AC] text-[#32B4AC]" : "border-transparent text-gray-400 hover:text-gray-600"
          }`} data-testid="view-map-btn">
          <BarChart3 className="w-3.5 h-3.5" /> Heatmap
        </button>
        <button onClick={() => { setShowTrends(true); setShowMap(false); }}
          className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5 -mb-px ${
            showTrends ? "border-[#32B4AC] text-[#32B4AC]" : "border-transparent text-gray-400 hover:text-gray-600"
          }`} data-testid="view-trends-btn">
          <TrendingUp className="w-3.5 h-3.5" /> Trends
        </button>
        <div className="ml-auto pb-2">
          <button onClick={handleExportCSV} className="text-xs px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-1.5 transition-colors"
            data-testid="pg-export-csv">
            <Download className="w-3 h-3" /> Export CSV
          </button>
        </div>
      </div>

      {showMap && !showTrends && (
        <div className="mb-6" data-testid="heatmap-view">
          {/* State-level Heatmap Analysis */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/60 flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Breach Heatmap by State</span>
              <span className="text-[10px] text-gray-400">{Object.keys(breachByState).length} states with breaches</span>
            </div>
            {Object.keys(breachByState).length === 0 ? (
              <div className="p-12 text-center">
                <CheckCircle2 className="w-10 h-10 text-emerald-300 mx-auto mb-3" />
                <div className="text-sm font-medium text-gray-500">No breaches detected</div>
                <div className="text-xs text-gray-400 mt-1">All prices are within MAP limits across all states</div>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {Object.entries(breachByState)
                  .sort(([, a], [, b]) => b - a)
                  .map(([state, count]) => {
                    const maxBreaches = Math.max(...Object.values(breachByState));
                    const intensity = count / maxBreaches;
                    const stateDetails = pincodeDetailsByState[state] || [];
                    const breachEntries = stateDetails.filter(d => d.breach);
                    const okEntries = stateDetails.filter(d => !d.breach);
                    const barColor = intensity > 0.7 ? "bg-red-500" : intensity > 0.3 ? "bg-amber-500" : "bg-yellow-500";
                    const isExpanded = expandedStates[state] || false;
                    // Group breaches by pincode for drill-down
                    const byPincode = {};
                    stateDetails.forEach(d => {
                      if (!byPincode[d.pincode]) byPincode[d.pincode] = [];
                      byPincode[d.pincode].push(d);
                    });
                    return (
                      <div key={state} data-testid={`heatmap-state-${state}`}>
                        <div className={`px-5 py-3.5 cursor-pointer hover:bg-gray-50 transition-colors flex items-center gap-4 ${
                          intensity > 0.7 ? "border-l-4 border-l-red-500" : intensity > 0.3 ? "border-l-4 border-l-amber-500" : "border-l-4 border-l-yellow-500"
                        }`} onClick={() => setExpandedStates(prev => ({ ...prev, [state]: !prev[state] }))}>
                          <div className="shrink-0 text-gray-400 transition-transform duration-200" style={{ transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)" }}>
                            <ChevronRight className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-1">
                              <span className="text-sm font-semibold text-gray-900">{state}</span>
                              <span className={`text-[10px] font-bold tabular-nums px-2 py-0.5 rounded ${
                                intensity > 0.7 ? "bg-red-100 text-red-700" : intensity > 0.3 ? "bg-amber-100 text-amber-700" : "bg-yellow-100 text-yellow-700"
                              }`}>{count} breach{count !== 1 ? "es" : ""}</span>
                            </div>
                            <div className="w-full max-w-xs h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${Math.max(intensity * 100, 8)}%` }} />
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-[10px] text-gray-400 shrink-0">
                            <span>{[...new Set(breachEntries.map(e => e.pincode))].length} pincodes</span>
                            <span>{okEntries.length} compliant</span>
                          </div>
                        </div>
                        {/* Drill-down: Pincode-level table */}
                        {isExpanded && (
                          <div className="bg-gray-50/60 border-t border-gray-100 px-5 py-3">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="border-b border-gray-200">
                                  <th className="text-left py-2 pr-4 text-[10px] font-medium text-gray-400 uppercase tracking-wider">Pincode</th>
                                  <th className="text-left py-2 pr-4 text-[10px] font-medium text-gray-400 uppercase tracking-wider">Product</th>
                                  <th className="text-left py-2 pr-4 text-[10px] font-medium text-gray-400 uppercase tracking-wider">Platform</th>
                                  <th className="text-right py-2 pr-4 text-[10px] font-medium text-gray-400 uppercase tracking-wider">Price</th>
                                  <th className="text-right py-2 pr-4 text-[10px] font-medium text-gray-400 uppercase tracking-wider">MAP</th>
                                  <th className="text-right py-2 pr-4 text-[10px] font-medium text-gray-400 uppercase tracking-wider">Gap</th>
                                  <th className="text-center py-2 pr-4 text-[10px] font-medium text-gray-400 uppercase tracking-wider">Status</th>
                                  <th className="text-right py-2 text-[10px] font-medium text-gray-400 uppercase tracking-wider">Scraped At</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(byPincode).sort(([a], [b]) => a.localeCompare(b)).map(([pc, details]) =>
                                  details.map((d, di) => {
                                    const gap = d.decided_price && d.price ? d.price - d.decided_price : null;
                                    return (
                                      <tr key={`${pc}-${di}`} className={`border-b border-gray-100 ${d.breach ? "bg-red-50/50" : ""}`}>
                                        {di === 0 ? (
                                          <td className="py-2 pr-4 font-mono font-medium text-gray-700" rowSpan={details.length}>{pc}</td>
                                        ) : null}
                                        <td className="py-2 pr-4 text-gray-700 truncate max-w-[200px]">{d.product}</td>
                                        <td className="py-2 pr-4">
                                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                            d.platform === "amazon" ? "bg-orange-100 text-orange-600" : "bg-yellow-100 text-yellow-700"
                                          }`}>{d.platform === "amazon" ? "AMZ" : "FK"}</span>
                                        </td>
                                        <td className="py-2 pr-4 text-right font-semibold tabular-nums text-gray-800">{formatCurrency(d.price)}</td>
                                        <td className="py-2 pr-4 text-right tabular-nums text-gray-500">{formatCurrency(d.decided_price)}</td>
                                        <td className="py-2 pr-4 text-right tabular-nums">
                                          {gap !== null ? (
                                            <span className={gap < 0 ? "text-red-600 font-semibold" : gap > 0 ? "text-emerald-600" : "text-gray-400"}>
                                              {gap < 0 ? `-${formatCurrency(Math.abs(gap))}` : gap > 0 ? `+${formatCurrency(gap)}` : "-"}
                                            </span>
                                          ) : <span className="text-gray-300">-</span>}
                                        </td>
                                        <td className="py-2 text-center">
                                          {d.breach ? (
                                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold">BREACH</span>
                                          ) : (
                                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">OK</span>
                                          )}
                                        </td>
                                        <td className="py-2 text-right text-[10px] text-gray-400 whitespace-nowrap tabular-nums">
                                          {d.scraped_at || "-"}
                                        </td>
                                      </tr>
                                    );
                                  })
                                )}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

          {/* Product × Platform Heatmap */}
          {Object.keys(byProduct).length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mt-4">
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/60">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Product × Platform Breach Summary</span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/30">
                    <th className="text-left px-5 py-2.5 text-[10px] font-medium text-gray-400 uppercase tracking-wider">Product</th>
                    <th className="text-center px-4 py-2.5 text-[10px] font-medium text-orange-500 uppercase tracking-wider">Amazon</th>
                    <th className="text-center px-4 py-2.5 text-[10px] font-medium text-yellow-600 uppercase tracking-wider">Flipkart</th>
                    <th className="text-center px-4 py-2.5 text-[10px] font-medium text-gray-400 uppercase tracking-wider">Total</th>
                    <th className="text-center px-4 py-2.5 text-[10px] font-medium text-gray-400 uppercase tracking-wider">Severity</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(byProduct).map(([productId, { info, entries }]) => {
                    const dp = info.decided_prices || {};
                    let amzBreachCount = 0, fkBreachCount = 0, amzTotal = 0, fkTotal = 0;
                    allPincodes.forEach(pc => {
                      const comp = getComparison(entries, pc, dp);
                      if (comp.amzPrice !== null && comp.amzDecided !== null) { amzTotal++; if (comp.amzBreach) amzBreachCount++; }
                      if (comp.fkPrice !== null && comp.fkDecided !== null) { fkTotal++; if (comp.fkBreach) fkBreachCount++; }
                    });
                    const totalBr = amzBreachCount + fkBreachCount;
                    const totalChecks = amzTotal + fkTotal;
                    const severity = totalChecks > 0 ? totalBr / totalChecks : 0;
                    return (
                      <tr key={productId} className="border-b border-gray-50 hover:bg-gray-50/50">
                        <td className="px-5 py-2.5 text-xs font-medium text-gray-900">{info.product_name}</td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`text-xs font-semibold tabular-nums ${amzBreachCount > 0 ? "text-red-600" : "text-emerald-600"}`}>
                            {amzBreachCount}/{amzTotal}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`text-xs font-semibold tabular-nums ${fkBreachCount > 0 ? "text-red-600" : "text-emerald-600"}`}>
                            {fkBreachCount}/{fkTotal}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`text-xs font-bold tabular-nums ${totalBr > 0 ? "text-red-600" : "text-emerald-600"}`}>{totalBr}</span>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${severity > 0.5 ? "bg-red-500" : severity > 0.2 ? "bg-amber-500" : severity > 0 ? "bg-yellow-500" : "bg-emerald-500"}`}
                                style={{ width: `${Math.max(severity * 100, severity > 0 ? 8 : 100)}%` }} />
                            </div>
                            <span className="text-[10px] text-gray-400 tabular-nums">{(severity * 100).toFixed(0)}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showTrends && (
        <div className="mb-6"><PriceTrendChart productIds={Object.keys(byProduct)} tracking={tracking} /></div>
      )}

      {!showMap && !showTrends && (
        <div className="space-y-3">
          {Object.entries(byProduct).map(([productId, { info, entries }]) => {
            const dp = info.decided_prices || {};
            const isExpanded = expandedProducts[productId] || false;
            // Compute summary stats for collapsed view
            let skuBreaches = 0, skuOk = 0, minAmz = Infinity, maxAmz = 0, minFk = Infinity, maxFk = 0;
            allPincodes.forEach(pc => {
              const comp = getComparison(entries, pc, dp);
              if (comp.amzBreach || comp.fkBreach) skuBreaches++;
              else if (comp.amzDecided || comp.fkDecided) skuOk++;
              if (comp.amzPrice !== null) { minAmz = Math.min(minAmz, comp.amzPrice); maxAmz = Math.max(maxAmz, comp.amzPrice); }
              if (comp.fkPrice !== null) { minFk = Math.min(minFk, comp.fkPrice); maxFk = Math.max(maxFk, comp.fkPrice); }
            });
            return (
              <div key={productId} className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid={`pg-product-${productId}`}>
                <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100 bg-gray-50/60 cursor-pointer hover:bg-gray-100/60 transition-colors"
                  onClick={() => setExpandedProducts(prev => ({ ...prev, [productId]: !prev[productId] }))}
                  data-testid={`pg-product-toggle-${productId}`}>
                  <div className="shrink-0 text-gray-400 transition-transform duration-200" style={{ transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)" }}>
                    <ChevronRight className="w-4 h-4" />
                  </div>
                  {info.image_url ? (
                    <img src={info.image_url} alt="" className="w-9 h-9 rounded-lg object-cover border border-gray-200" />
                  ) : (
                    <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center"><Package className="w-4 h-4 text-gray-400" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-gray-900 truncate">{info.product_name}</div>
                    <div className="text-[10px] text-gray-400">{info.brand_name}</div>
                  </div>
                  {/* Collapsed summary badges */}
                  <div className="flex items-center gap-2 text-[10px] shrink-0">
                    {dp.amazon && <span className="px-2 py-0.5 rounded bg-orange-50 text-orange-600 border border-orange-200">AMZ MAP: {formatCurrency(parseFloat(dp.amazon))}</span>}
                    {dp.flipkart && <span className="px-2 py-0.5 rounded bg-yellow-50 text-yellow-700 border border-yellow-200">FK MAP: {formatCurrency(parseFloat(dp.flipkart))}</span>}
                    {skuBreaches > 0 ? (
                      <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 border border-red-200 font-semibold">{skuBreaches} breach{skuBreaches !== 1 ? "es" : ""}</span>
                    ) : (dp.amazon || dp.flipkart) ? (
                      <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200 font-medium">All OK</span>
                    ) : null}
                    <span className="text-gray-400">{allPincodes.length} pincodes</span>
                  </div>
                </div>
                {isExpanded && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100 bg-gray-50/30">
                          <th className="text-left px-4 py-2.5 text-[10px] font-medium text-gray-400 uppercase tracking-wider w-24">Pincode</th>
                          <th className="text-right px-4 py-2.5 text-[10px] font-medium text-orange-500 uppercase tracking-wider">Amazon</th>
                          <th className="text-right px-4 py-2.5 text-[10px] font-medium text-yellow-600 uppercase tracking-wider">Flipkart</th>
                          <th className="text-right px-4 py-2.5 text-[10px] font-medium text-amber-500 uppercase tracking-wider">Blinkit</th>
                          <th className="text-right px-4 py-2.5 text-[10px] font-medium text-gray-400 uppercase tracking-wider w-28">Diff</th>
                          <th className="text-center px-4 py-2.5 text-[10px] font-medium text-gray-400 uppercase tracking-wider w-24">Status</th>
                          <th className="text-right px-4 py-2.5 text-[10px] font-medium text-gray-400 uppercase tracking-wider w-40">Scraped At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allPincodes.map(pc => {
                          const comp = getComparison(entries, pc, dp);
                          const hasBreach = comp.amzBreach || comp.fkBreach || comp.bkBreach;
                          return (
                            <tr key={pc} className={`border-b border-gray-50 transition-colors ${hasBreach ? "bg-red-50/40" : "hover:bg-gray-50/50"}`}>
                              <td className="px-4 py-2.5 font-mono text-xs text-gray-700 font-medium">{pc}</td>
                              <td className="px-4 py-2.5 text-right">
                                <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold tabular-nums ${
                                  comp.amzBreach ? "bg-red-100 text-red-700 border border-red-200" : comp.amzPrice !== null ? "text-gray-800" : "text-gray-300"
                                }`}>
                                  {comp.amzPrice !== null ? formatCurrency(comp.amzPrice) : "N/A"}
                                  {comp.amzBreach && <ShieldAlert className="w-3 h-3 text-red-500" />}
                                </div>
                              </td>
                              <td className="px-4 py-2.5 text-right">
                                <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold tabular-nums ${
                                  comp.fkBreach ? "bg-red-100 text-red-700 border border-red-200" : comp.fkPrice !== null ? "text-gray-800" : "text-gray-300"
                                }`}>
                                  {comp.fkPrice !== null ? formatCurrency(comp.fkPrice) : "N/A"}
                                  {comp.fkBreach && <ShieldAlert className="w-3 h-3 text-red-500" />}
                                </div>
                              </td>
                              <td className="px-4 py-2.5 text-right">
                                <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold tabular-nums ${
                                  comp.bkBreach ? "bg-red-100 text-red-700 border border-red-200" : comp.bkPrice !== null ? "text-gray-800" : "text-gray-300"
                                }`}>
                                  {comp.bkPrice !== null ? formatCurrency(comp.bkPrice) : "N/A"}
                                  {comp.bkBreach && <ShieldAlert className="w-3 h-3 text-red-500" />}
                                </div>
                              </td>
                              <td className="px-4 py-2.5 text-right text-xs tabular-nums">
                                {comp.diff !== null ? (
                                  <span className={comp.diff === 0 ? "text-gray-400" : "text-amber-600 font-medium"}>
                                    {comp.diff === 0 ? "Same" : `${formatCurrency(comp.diff)}`}
                                    {comp.diffPct && comp.diff > 0 && <span className="text-gray-400 ml-1">({comp.diffPct}%)</span>}
                                  </span>
                                ) : <span className="text-gray-300">-</span>}
                              </td>
                              <td className="px-4 py-2.5 text-center">
                                {hasBreach ? (
                                  <span className="text-[10px] px-2 py-0.5 rounded bg-red-100 text-red-700 font-semibold uppercase">Breach</span>
                                ) : (comp.amzDecided || comp.fkDecided || comp.bkDecided) ? (
                                  <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium">OK</span>
                                ) : (
                                  <span className="text-[10px] px-2 py-0.5 rounded bg-gray-100 text-gray-500">No MAP</span>
                                )}
                              </td>
                              <td className="px-4 py-2.5 text-right text-[10px] text-gray-400 whitespace-nowrap tabular-nums">
                                {comp.scrapedAt || "-"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


// ─── PRICE PULSE WIDGET ───
const PricePulseWidget = ({ recentRuns }) => {
  const [pulseData, setPulseData] = useState([]);

  useEffect(() => {
    // Build pulse items from recent runs
    const items = [];
    recentRuns.forEach(run => {
      if (run.status !== "completed") return;
      const timeAgo = getTimeAgo(new Date(run.created_at));
      if (run.breach_count > 0) {
        items.push({ type: "breach", text: `${run.breach_count} breach${run.breach_count > 1 ? "es" : ""} detected`, detail: `${run.product_count || "?"} products · ${run.pincode_count || "?"} pincodes`, time: timeAgo, icon: "alert" });
      } else if (run.result_count > 0) {
        items.push({ type: "ok", text: `All prices compliant`, detail: `${run.product_count || "?"} products · ${run.pincode_count || "?"} pincodes`, time: timeAgo, icon: "check" });
      }
      if (run.result_count > 0) {
        items.push({ type: "scan", text: `${run.result_count} prices collected`, detail: run.progress_message || "Scan complete", time: timeAgo, icon: "scan" });
      }
    });
    setPulseData(items.slice(0, 12));
  }, [recentRuns]);

  if (pulseData.length === 0) return null;

  // Duplicate items for seamless scrolling
  const tickerItems = [...pulseData, ...pulseData];

  return (
    <div className="mb-6 overflow-hidden rounded-lg border border-gray-200 bg-white" data-testid="price-pulse-widget">
      <div className="flex items-center">
        <div className="shrink-0 px-4 py-2.5 bg-gray-900 text-white flex items-center gap-2 z-10">
          <Activity className="w-3.5 h-3.5 text-[#32B4AC]" />
          <span className="text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">Price Pulse</span>
        </div>
        <div className="flex-1 overflow-hidden relative">
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent z-10" />
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent z-10" />
          <div className="flex items-center gap-6 animate-ticker py-2.5 px-4">
            {tickerItems.map((item, i) => (
              <div key={i} className="flex items-center gap-2 whitespace-nowrap shrink-0">
                {item.icon === "alert" && <ShieldAlert className="w-3 h-3 text-red-500" />}
                {item.icon === "check" && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
                {item.icon === "scan" && <BarChart3 className="w-3 h-3 text-blue-500" />}
                <span className={`text-xs font-medium ${item.type === "breach" ? "text-red-600" : item.type === "ok" ? "text-emerald-600" : "text-gray-700"}`}>
                  {item.text}
                </span>
                <span className="text-[10px] text-gray-400">{item.detail}</span>
                <span className="text-[10px] text-gray-300">{item.time}</span>
                <span className="text-gray-200 mx-1">|</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};


// ════════════════════════════════════════════════════════════
// ─── HUB VIEW: CONTROL CENTER ───
// ════════════════════════════════════════════════════════════
const HubView = ({ schedules, recentRuns, loadingRun, onStartScan, onLoadRun, onDeleteSchedule, onToggleSchedule }) => {
  // Compute stats from recent runs
  const totalScans = recentRuns.length;
  const totalBreaches = recentRuns.reduce((sum, r) => sum + (r.breach_count || 0), 0);
  const lastRun = recentRuns.length > 0 ? getTimeAgo(new Date(recentRuns[0].created_at)) : "Never";
  const activeSchedules = schedules.filter(s => s.active).length;

  return (
    <div data-testid="pg-hub">
      {/* Price Pulse Ticker */}
      <PricePulseWidget recentRuns={recentRuns} />

      {/* Quick Stats Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
            <BarChart3 className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <div className="text-xl font-bold text-gray-900 tabular-nums">{totalScans}</div>
            <div className="text-[11px] text-gray-400 uppercase tracking-wide">Total Scans</div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${totalBreaches > 0 ? "bg-red-50" : "bg-emerald-50"}`}>
            <ShieldAlert className={`w-5 h-5 ${totalBreaches > 0 ? "text-red-500" : "text-emerald-500"}`} />
          </div>
          <div>
            <div className={`text-xl font-bold tabular-nums ${totalBreaches > 0 ? "text-red-600" : "text-emerald-600"}`}>{totalBreaches}</div>
            <div className="text-[11px] text-gray-400 uppercase tracking-wide">Total Breaches</div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center shrink-0">
            <Clock className="w-5 h-5 text-gray-400" />
          </div>
          <div>
            <div className="text-sm font-bold text-gray-900">{lastRun}</div>
            <div className="text-[11px] text-gray-400 uppercase tracking-wide">Last Run</div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#F0FDFA] flex items-center justify-center shrink-0">
            <Activity className="w-5 h-5 text-[#32B4AC]" />
          </div>
          <div>
            <div className="text-xl font-bold text-gray-900 tabular-nums">{activeSchedules}</div>
            <div className="text-[11px] text-gray-400 uppercase tracking-wide">Active Schedules</div>
          </div>
        </div>
      </div>

      {/* Two-Column Layout: Schedules + Recent Analyses */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

        {/* Left: Scheduled Runs (4 cols) */}
        <div className="lg:col-span-4" data-testid="schedules-panel">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden h-full">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#32B4AC]" />
                <h3 className="text-sm font-semibold text-gray-900">Scheduled Runs</h3>
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded bg-[#F0FDFA] text-[#32B4AC] font-semibold tabular-nums">
                {activeSchedules} active
              </span>
            </div>
            <div className="p-4">
              {schedules.length === 0 ? (
                <div className="text-center py-10">
                  <Calendar className="w-8 h-8 text-gray-200 mx-auto mb-3" />
                  <div className="text-sm text-gray-400 font-medium">No schedules yet</div>
                  <div className="text-xs text-gray-300 mt-1">Start a scan and schedule it to repeat</div>
                </div>
              ) : (
                <div className="space-y-3">
                  {schedules.map(sch => {
                    const nextRun = sch.next_run ? new Date(sch.next_run) : null;
                    return (
                      <div key={sch.schedule_id}
                        className={`p-3.5 rounded-lg border transition-all duration-150 ${
                          sch.active
                            ? "border-[#32B4AC]/20 bg-[#F0FDFA]/50"
                            : "border-gray-100 bg-gray-50/50 opacity-60"
                        }`}
                        data-testid={`schedule-${sch.schedule_id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-gray-900 truncate flex-1 mr-2">{sch.name}</span>
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => onToggleSchedule(sch.schedule_id, !sch.active)}
                              className={`p-1.5 rounded-lg transition-colors ${sch.active ? "text-[#32B4AC] hover:bg-[#32B4AC]/10" : "text-gray-400 hover:bg-gray-100"}`}
                              data-testid={`toggle-schedule-${sch.schedule_id}`}>
                              {sch.active ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                            </button>
                            <button onClick={() => onDeleteSchedule(sch.schedule_id)}
                              className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                              data-testid={`delete-schedule-${sch.schedule_id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>

                        {/* Schedule Details */}
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2 text-[11px] text-gray-500">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3 text-gray-400" />
                              <span>{(sch.days || []).map(d => DAY_LABELS[d]).join(", ")}</span>
                            </div>
                            <span className="text-gray-300">|</span>
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3 text-gray-400" />
                              <span>{sch.time_of_day} IST</span>
                            </div>
                          </div>
                          <div className="text-[11px] text-gray-400">
                            {sch.scope === "brand" ? "Entire brand" : `${(sch.product_ids || []).length} products`} · {(sch.pincodes || []).length} pincodes
                          </div>
                          {nextRun && sch.active && (
                            <div className="flex items-center gap-1.5 mt-1">
                              <Zap className="w-3 h-3 text-[#32B4AC]" />
                              <span className="text-[11px] text-[#32B4AC] font-medium">
                                Next: {nextRun.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                              </span>
                            </div>
                          )}
                          {sch.last_run && (
                            <div className="text-[10px] text-gray-300">
                              Last: {new Date(sch.last_run).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: Recent Analyses (8 cols) */}
        <div className="lg:col-span-8" data-testid="recent-analyses-panel">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden h-full">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900">Recent Analyses</h3>
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded bg-gray-100 text-gray-500 font-medium tabular-nums">
                {recentRuns.length} runs
              </span>
            </div>

            {recentRuns.length === 0 ? (
              <div className="text-center py-16">
                <ShieldCheck className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                <div className="text-sm text-gray-400 font-medium">No analyses yet</div>
                <div className="text-xs text-gray-300 mt-1">Start your first scan to see results here</div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="recent-analyses-table">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/40">
                      <th className="text-left px-5 py-2.5 text-[10px] font-medium text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="text-left px-4 py-2.5 text-[10px] font-medium text-gray-400 uppercase tracking-wider">Summary</th>
                      <th className="text-right px-4 py-2.5 text-[10px] font-medium text-gray-400 uppercase tracking-wider">Products</th>
                      <th className="text-right px-4 py-2.5 text-[10px] font-medium text-gray-400 uppercase tracking-wider">Pincodes</th>
                      <th className="text-right px-4 py-2.5 text-[10px] font-medium text-gray-400 uppercase tracking-wider">Results</th>
                      <th className="text-right px-4 py-2.5 text-[10px] font-medium text-gray-400 uppercase tracking-wider">Breaches</th>
                      <th className="text-right px-4 py-2.5 text-[10px] font-medium text-gray-400 uppercase tracking-wider">When</th>
                      <th className="text-center px-3 py-2.5 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentRuns.map(run => {
                      const createdAt = new Date(run.created_at);
                      const isProcessing = run.status === "processing";
                      return (
                        <tr key={run.job_id}
                          onClick={() => !isProcessing && !loadingRun && onLoadRun(run.job_id)}
                          className={`border-b border-gray-50 transition-colors ${
                            isProcessing ? "opacity-50" : "hover:bg-[#F0FDFA]/40 cursor-pointer"
                          }`}
                          data-testid={`recent-run-${run.job_id}`}>
                          <td className="px-5 py-3">
                            <span className={`text-[10px] px-2 py-1 rounded font-semibold uppercase ${
                              isProcessing ? "bg-blue-100 text-blue-700" :
                              run.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                              run.status === "stopped" ? "bg-amber-100 text-amber-700" :
                              "bg-red-100 text-red-700"
                            }`}>
                              {isProcessing ? "Running" : run.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-xs font-medium text-gray-800 truncate max-w-[200px]">
                              {run.progress_message || "Price Governance Scan"}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right text-xs text-gray-600 tabular-nums">{run.product_count || 0}</td>
                          <td className="px-4 py-3 text-right text-xs text-gray-600 tabular-nums">{run.pincode_count || 0}</td>
                          <td className="px-4 py-3 text-right text-xs text-gray-600 tabular-nums">{run.result_count || 0}</td>
                          <td className="px-4 py-3 text-right">
                            {!isProcessing && (
                              run.breach_count > 0 ? (
                                <span className="text-xs font-bold text-red-600 tabular-nums">{run.breach_count}</span>
                              ) : (
                                <span className="text-xs text-emerald-600 font-medium">0</span>
                              )
                            )}
                          </td>
                          <td className="px-4 py-3 text-right text-[11px] text-gray-400 whitespace-nowrap" title={createdAt.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}>
                            {createdAt.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" })}
                          </td>
                          <td className="px-3 py-3 text-center">
                            {!isProcessing && <Eye className="w-3.5 h-3.5 text-gray-300" />}
                            {isProcessing && <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-400" />}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


// ════════════════════════════════════════════════════════════
// ─── MAIN PRICE GOVERNANCE COMPONENT ───
// ════════════════════════════════════════════════════════════
export const PriceGovernance = ({ user, onNavigate, onLogout }) => {
  // View mode: "hub" (dashboard) or "wizard" (4-step scan)
  const [viewMode, setViewMode] = useState("hub");
  const [step, setStep] = useState(1);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [allPincodes, setAllPincodes] = useState([]);
  const [selectedPincodes, setSelectedPincodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pgIconUrl, setPgIconUrl] = useState("");
  const [tracking, setTracking] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [results, setResults] = useState([]);
  const [jobStatus, setJobStatus] = useState(null);
  const [progressMsg, setProgressMsg] = useState("");
  const [trackingManifest, setTrackingManifest] = useState([]);
  const pollRef = useRef(null);

  // Scheduling state
  const [schedules, setSchedules] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    name: "", days: [], time_of_day: "09:00", scope: "custom",
  });

  // Recent runs state
  const [recentRuns, setRecentRuns] = useState([]);
  const [loadingRun, setLoadingRun] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [brandsRes, pincodesRes, platformsRes, schedulesRes, pgJobsRes] = await Promise.all([
          axios.get(`${API}/brands`),
          axios.get(`${API}/pincodes`),
          axios.get(`${API}/platforms`),
          axios.get(`${API}/price-governance/schedules`),
          axios.get(`${API}/price-governance/jobs`),
        ]);
        const pgPlatform = (platformsRes.data || []).find(p => p.platform_id === 'price-governance');
        if (pgPlatform?.icon_url) setPgIconUrl(pgPlatform.icon_url);
        const productsRes = await axios.get(`${API}/products`);
        const brandCounts = {};
        (productsRes.data || []).forEach(p => {
          brandCounts[p.brand_id] = (brandCounts[p.brand_id] || 0) + 1;
        });
        setBrands((brandsRes.data || []).map(b => ({ ...b, product_count: brandCounts[b.brand_id] || 0 })));
        setAllPincodes(pincodesRes.data || []);
        setSchedules(schedulesRes.data || []);
        setRecentRuns(pgJobsRes.data || []);
      } catch { toast.error("Failed to load data"); }
      setLoading(false);
    };
    fetchData();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const handleBrandSelect = async (brand) => {
    setSelectedBrand(brand);
    setSelectedProducts([]);
    try {
      const res = await axios.get(`${API}/products`);
      const brandProducts = (res.data || []).filter(p => p.brand_id === brand.brand_id);
      setProducts(brandProducts);
    } catch { toast.error("Failed to load products"); }
  };

  const toggleProduct = (id, batchIds) => {
    if (id === "__batch_select__" && batchIds) {
      setSelectedProducts(prev => [...new Set([...prev, ...batchIds])]);
    } else if (id === "__batch_deselect__" && batchIds) {
      const toRemove = new Set(batchIds);
      setSelectedProducts(prev => prev.filter(p => !toRemove.has(p)));
    } else {
      setSelectedProducts(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
    }
  };
  const selectAllProducts = () => {
    if (selectedProducts.length === products.length) setSelectedProducts([]);
    else setSelectedProducts(products.map(p => p.product_id));
  };

  const togglePincode = (pc) => {
    setSelectedPincodes(prev => prev.includes(pc) ? prev.filter(p => p !== pc) : [...prev, pc]);
  };
  const toggleCity = (cityPins) => {
    const allSelected = cityPins.every(pc => selectedPincodes.includes(pc));
    if (allSelected) setSelectedPincodes(prev => prev.filter(p => !cityPins.includes(p)));
    else setSelectedPincodes(prev => [...new Set([...prev, ...cityPins])]);
  };
  const toggleState = (state, statePins) => {
    const allSelected = statePins.every(pc => selectedPincodes.includes(pc));
    if (allSelected) setSelectedPincodes(prev => prev.filter(p => !statePins.includes(p)));
    else setSelectedPincodes(prev => [...new Set([...prev, ...statePins])]);
  };

  const startScanWizard = () => {
    setViewMode("wizard");
    setStep(1);
    setSelectedBrand(null);
    setSelectedProducts([]);
    setSelectedPincodes([]);
    setResults([]);
    setJobId(null);
    setJobStatus(null);
  };

  const backToHub = () => {
    setViewMode("hub");
    // Refresh recent runs
    axios.get(`${API}/price-governance/jobs`).then(res => setRecentRuns(res.data || [])).catch(() => {});
  };

  const startTracking = async () => {
    if (selectedProducts.length === 0) return toast.error("Select at least one product");
    if (selectedPincodes.length === 0) return toast.error("Select at least one pincode");
    setTracking(true);
    try {
      const res = await axios.post(`${API}/price-governance/track`, {
        product_ids: selectedProducts,
        pincodes: selectedPincodes,
      });
      setJobId(res.data.job_id);
      setJobStatus("processing");
      setStep(4);
      pollRef.current = setInterval(async () => {
        try {
          const jr = await axios.get(`${API}/jobs/${res.data.job_id}`);
          setResults(jr.data.results || []);
          setProgressMsg(jr.data.progress_message || "");
          setTrackingManifest(jr.data.tracking || []);
          if (jr.data.status !== "processing") {
            setJobStatus(jr.data.status);
            clearInterval(pollRef.current);
          }
        } catch { /* ignore */ }
      }, 3000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start tracking");
    }
    setTracking(false);
  };

  // Schedule CRUD
  const handleCreateSchedule = async () => {
    if (!scheduleForm.name.trim()) return toast.error("Name is required");
    if (scheduleForm.days.length === 0) return toast.error("Select at least one day");
    try {
      const payload = {
        ...scheduleForm,
        brand_id: selectedBrand?.brand_id,
        product_ids: scheduleForm.scope === "brand" ? [] : selectedProducts,
        pincodes: selectedPincodes,
      };
      const res = await axios.post(`${API}/price-governance/schedules`, payload);
      setSchedules(prev => [res.data, ...prev]);
      setShowScheduleModal(false);
      setScheduleForm({ name: "", days: [], time_of_day: "09:00", scope: "custom" });
      toast.success("Schedule created");
    } catch { toast.error("Failed to create schedule"); }
  };

  const handleDeleteSchedule = async (id) => {
    if (!window.confirm("Delete this schedule?")) return;
    try {
      await axios.delete(`${API}/price-governance/schedules/${id}`);
      setSchedules(prev => prev.filter(s => s.schedule_id !== id));
      toast.success("Schedule deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const handleToggleSchedule = async (id, active) => {
    try {
      await axios.put(`${API}/price-governance/schedules/${id}`, { active });
      setSchedules(prev => prev.map(s => s.schedule_id === id ? { ...s, active } : s));
    } catch { toast.error("Failed to update"); }
  };

  const toggleScheduleDay = (day) => {
    setScheduleForm(prev => ({
      ...prev,
      days: prev.days.includes(day) ? prev.days.filter(d => d !== day) : [...prev.days, day].sort(),
    }));
  };

  const loadPastRun = async (runJobId) => {
    setLoadingRun(true);
    try {
      const res = await axios.get(`${API}/jobs/${runJobId}`);
      setResults(res.data.results || []);
      setTrackingManifest(res.data.tracking || []);
      setJobId(res.data.job_id);
      setJobStatus(res.data.status);
      setProgressMsg(res.data.progress_message || "Past analysis");
      setViewMode("wizard");
      setStep(4);
    } catch {
      toast.error("Failed to load past run");
    }
    setLoadingRun(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#32B4AC]" />
    </div>
  );

  const nextScheduledRun = schedules
    .filter(s => s.active && s.next_run)
    .map(s => ({ ...s, next_run_dt: new Date(s.next_run) }))
    .sort((a, b) => a.next_run_dt - b.next_run_dt)[0];

  const STEPS = [
    { num: 1, label: "Select Brand" },
    { num: 2, label: "Choose Products" },
    { num: 3, label: "Select Pincodes" },
    { num: 4, label: "Breach Analysis" },
  ];

  return (
    <div className="min-h-screen bg-[#FAFBFC]" data-testid="price-governance">
      <AppHeader
        user={user}
        breadcrumbs={[
          { label: "Platforms", onClick: () => onNavigate("/select-platform") },
          {
            label: "Price Governance",
            icon: pgIconUrl ? (
              <img src={pgIconUrl} alt="" className="w-4 h-4 object-contain rounded" />
            ) : (
              <ShieldCheck className="w-4 h-4 text-[#32B4AC]" />
            ),
            onClick: viewMode === "wizard" ? backToHub : undefined,
          },
          ...(viewMode === "wizard" ? [{ label: step === 4 ? "Breach Analysis" : "New Scan" }] : []),
        ]}
        onNavigate={onNavigate}
        onLogout={onLogout}
        rightContent={
          nextScheduledRun ? (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F0FDFA] border border-[#32B4AC]/20 rounded-lg" data-testid="next-schedule-badge">
              <Clock className="w-3.5 h-3.5 text-[#32B4AC]" />
              <span className="text-[11px] text-[#32B4AC] font-medium">
                Next: {nextScheduledRun.next_run_dt.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
          ) : null
        }
      />

      <div className="max-w-7xl mx-auto px-6 py-6">

        {/* ──────── HUB VIEW ──────── */}
        {viewMode === "hub" && (
          <>
            {/* Hero Title + Start Scan CTA */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                {pgIconUrl ? (
                  <img src={pgIconUrl} alt="" className="w-9 h-9 object-contain rounded-lg" />
                ) : (
                  <div className="w-9 h-9 rounded-lg bg-[#F0FDFA] flex items-center justify-center">
                    <ShieldCheck className="w-5 h-5 text-[#32B4AC]" />
                  </div>
                )}
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Price Governance</h1>
                  <p className="text-sm text-gray-400 mt-0.5">Monitor pricing compliance across platforms and pincodes</p>
                </div>
              </div>
              <Button onClick={startScanWizard} className="btn-accent gap-2 px-5" data-testid="start-scan-button">
                <Plus className="w-4 h-4" /> Start New Scan
              </Button>
            </div>

            <HubView
              schedules={schedules}
              recentRuns={recentRuns}
              loadingRun={loadingRun}
              onStartScan={startScanWizard}
              onLoadRun={loadPastRun}
              onDeleteSchedule={handleDeleteSchedule}
              onToggleSchedule={handleToggleSchedule}
            />
          </>
        )}

        {/* ──────── WIZARD VIEW ──────── */}
        {viewMode === "wizard" && (
          <>
            {/* Back to Hub + Stepper */}
            {step < 4 && (
              <div className="mb-6">
                <button onClick={backToHub} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 transition-colors mb-4"
                  data-testid="back-to-hub">
                  <ArrowLeft className="w-4 h-4" /> Back to Dashboard
                </button>

                {/* Stepper */}
                <div className="flex items-center gap-2">
                  {STEPS.filter(s => s.num <= 3).map((s, i) => (
                    <div key={s.num} className="flex items-center gap-2 flex-1">
                      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors w-full ${
                        step === s.num ? "bg-[#32B4AC] text-white" :
                        step > s.num ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-400"
                      }`}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          step > s.num ? "bg-emerald-500 text-white" : step === s.num ? "bg-white/20 text-white" : "bg-gray-200 text-gray-400"
                        }`}>
                          {step > s.num ? <Check className="w-3.5 h-3.5" /> : s.num}
                        </div>
                        <span className="text-xs font-medium hidden sm:block">{s.label}</span>
                      </div>
                      {i < 2 && <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 1: Brand Selection */}
            {step === 1 && (
              <div className="max-w-5xl mx-auto">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Select a Brand</h2>
                <p className="text-sm text-gray-500 mb-5">Choose the brand whose products you want to monitor for price breaches.</p>
                <BrandCards brands={brands} selectedBrand={selectedBrand} onSelect={handleBrandSelect} />
                <div className="flex justify-end mt-6">
                  <Button onClick={() => { if (!selectedBrand) return toast.error("Select a brand first"); setStep(2); }}
                    className="btn-accent gap-2" disabled={!selectedBrand} data-testid="pg-next-step2">
                    Next <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Product Selection */}
            {step === 2 && (
              <div className="max-w-5xl mx-auto">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">
                  Select Products — {selectedBrand?.name}
                </h2>
                <p className="text-sm text-gray-500 mb-5">
                  Products with MAP (Minimum Advertised Price) set will be checked for price breaches.
                </p>
                {products.length > 0 ? (
                  <ProductSelector products={products} selectedProducts={selectedProducts} onToggle={toggleProduct} onSelectAll={selectAllProducts} />
                ) : (
                  <div className="text-center py-12 text-gray-400 text-sm">No products found for this brand.</div>
                )}
                <div className="flex items-center justify-between mt-6">
                  <Button variant="outline" onClick={() => setStep(1)} className="gap-2" data-testid="pg-back-step1">
                    <ChevronLeft className="w-4 h-4" /> Back
                  </Button>
                  <Button onClick={() => { if (!selectedProducts.length) return toast.error("Select at least one product"); setStep(3); }}
                    className="btn-accent gap-2" disabled={!selectedProducts.length} data-testid="pg-next-step3">
                    Next <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Pincode Selection + Schedule */}
            {step === 3 && (
              <div className="max-w-5xl mx-auto">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Select Pincodes</h2>
                <p className="text-sm text-gray-500 mb-5">
                  Choose pincodes to check. You can also schedule this scan to run automatically.
                </p>
                <PincodeSelector pincodes={allPincodes} selectedPincodes={selectedPincodes}
                  onTogglePincode={togglePincode} onToggleCity={toggleCity} onToggleState={toggleState} />

                <div className="flex items-center justify-between mt-6">
                  <Button variant="outline" onClick={() => setStep(2)} className="gap-2" data-testid="pg-back-step2">
                    <ChevronLeft className="w-4 h-4" /> Back
                  </Button>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => setShowScheduleModal(true)} className="gap-2" disabled={!selectedPincodes.length}
                      data-testid="pg-schedule-btn">
                      <Calendar className="w-4 h-4" /> Schedule
                    </Button>
                    <Button onClick={startTracking} className="btn-accent gap-2" disabled={tracking || !selectedPincodes.length} data-testid="pg-start-tracking">
                      {tracking ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                      {tracking ? "Starting..." : "Run Now"}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Results */}
            {step === 4 && (
              <div className="max-w-6xl mx-auto">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <button onClick={backToHub} className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-600 transition-colors"
                        data-testid="results-back-to-hub">
                        <ArrowLeft className="w-4 h-4" />
                      </button>
                      <h2 className="text-lg font-semibold text-gray-900">Breach Analysis</h2>
                    </div>
                    <p className="text-sm text-gray-500 pl-6">{progressMsg}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {jobStatus !== "processing" && (
                      <Button onClick={startScanWizard} className="btn-accent gap-2" data-testid="pg-new-scan">
                        <Plus className="w-4 h-4" /> New Scan
                      </Button>
                    )}
                  </div>
                </div>

                {jobStatus === "processing" && (
                  <div className="bg-white rounded-lg border border-[#32B4AC]/20 p-8 text-center mb-6">
                    <Loader2 className="w-10 h-10 animate-spin text-[#32B4AC] mx-auto mb-4" />
                    <div className="text-sm text-gray-700 font-medium mb-1">Scanning prices across platforms...</div>
                    <div className="text-xs text-gray-400">{progressMsg}</div>
                    {results.length > 0 && (
                      <div className="mt-4 text-sm text-[#32B4AC] font-medium">{results.length} prices collected so far</div>
                    )}
                  </div>
                )}

                {results.length > 0 && (
                  <BreachResults results={results} tracking={trackingManifest} allPincodesMaster={allPincodes} />
                )}

                {jobStatus === "completed" && results.length === 0 && (
                  <div className="text-center py-12 text-gray-400 text-sm">
                    <AlertTriangle className="w-8 h-8 mx-auto mb-3 text-amber-400" />
                    No prices could be collected. Check product platform URLs in Control Panel.
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowScheduleModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="schedule-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-gray-900">Schedule Price Governance</h3>
              <button onClick={() => setShowScheduleModal(false)} className="p-1 rounded-lg hover:bg-gray-100">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1 block">Schedule Name</label>
                <Input value={scheduleForm.name} onChange={e => setScheduleForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Weekly North India Check" className="text-sm" data-testid="schedule-name-input" />
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2 block">Scope</label>
                <div className="flex gap-2">
                  <button onClick={() => setScheduleForm(f => ({ ...f, scope: "brand" }))}
                    className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                      scheduleForm.scope === "brand" ? "bg-[#32B4AC] text-white border-[#32B4AC]" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                    }`} data-testid="schedule-scope-brand">
                    Entire Brand
                  </button>
                  <button onClick={() => setScheduleForm(f => ({ ...f, scope: "custom" }))}
                    className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                      scheduleForm.scope === "custom" ? "bg-[#32B4AC] text-white border-[#32B4AC]" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                    }`} data-testid="schedule-scope-custom">
                    Selected Products
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2 block">Days</label>
                <div className="flex gap-1.5">
                  {DAY_LABELS.map((label, i) => (
                    <button key={i} onClick={() => toggleScheduleDay(i)}
                      className={`w-10 h-10 rounded-lg text-xs font-semibold transition-colors ${
                        scheduleForm.days.includes(i) ? "bg-[#32B4AC] text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                      }`} data-testid={`schedule-day-${i}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1 block">Time (IST)</label>
                <Input type="time" value={scheduleForm.time_of_day}
                  onChange={e => setScheduleForm(f => ({ ...f, time_of_day: e.target.value }))}
                  className="text-sm w-32" data-testid="schedule-time-input" />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowScheduleModal(false)} data-testid="schedule-cancel">Cancel</Button>
                <Button onClick={handleCreateSchedule} className="btn-accent" data-testid="schedule-create">Create Schedule</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceGovernance;
